import type { SelectionListItem } from '../../../../../../shared/selection-lists/types';
import type { GeographyMode } from '../../../../../../shared/user-preferences/types';
import type { MapPlotOptions } from '../../../PageConfigProvider/types/item';
import type { MetricValues } from '../../../PageConfigProvider/types/metric';
import { LocationType, type MapPoint, type MapRenderer } from '../types';
import { getColorScaleInfo } from '../utils/color-scale';
import { MAX_ZOOM, MIN_ZOOM } from '../utils/constants';
import { getMetricPaletteConfig } from '../utils/metric-scales';
import { type ChoroplethLayerConfig, LAYER_CONFIGS } from './ChoroplethConfig';
import {
  type LayerStatsByLocation,
  normalizeChoroplethData,
} from './dataManager';
import { LayerManager } from './layerManager';
import { buildFillAndOutline } from './paintExpressionBuilder';

const ALWAYS_SHOW_TYPES: readonly LocationType[] = [
  LocationType.METRO_AREA,
  LocationType.COUNTRY,
  LocationType.CITY,
];

export class ChoroplethRenderer implements MapRenderer {
  private map: mapboxgl.Map | null = null;

  private cleanupOverviewHandlers: (() => void) | undefined;

  private valueByLayerId: Map<string, Record<string, number>> = new Map();
  private tooltipByLayerId: Map<
    string,
    Record<string, Record<string, string | number>>
  > = new Map();

  private layerManager = new LayerManager();
  private hasNonMetroData = false;
  private isZooming = false;
  private isMapLoaded = false;
  private currentHighlight: {
    configId: string;
    key: string;
    featureId: string | number;
  } | null = null;
  private isInteractive = true;
  private readonly enabledConfigs: ChoroplethLayerConfig[];
  private readonly configBySourceId: Map<string, ChoroplethLayerConfig>;
  private readonly fillLayerIds: string[];

  private layerPriority: Record<string, number> = {
    [LocationType.CITY]: 7,
    [LocationType.COUNTY]: 6,
    [LocationType.METRO_AREA]: 6,
    [LocationType.STATE]: 5,
    [LocationType.COUNTRY]: 3,
    [LocationType.REGION]: 2,
  };

  constructor(geographyMode: GeographyMode = 'msa') {
    const allConfigs = LAYER_CONFIGS;

    if (geographyMode === 'administrative') {
      this.enabledConfigs = allConfigs.filter(
        (c) => c.id !== LocationType.METRO_AREA
      );
    } else {
      this.enabledConfigs = allConfigs.filter(
        (c) => c.id !== LocationType.STATE && c.id !== LocationType.COUNTY
      );
    }

    this.configBySourceId = new Map(
      this.enabledConfigs.map((cfg) => [cfg.sourceId, cfg])
    );
    this.fillLayerIds = this.enabledConfigs.map((cfg) => cfg.fillLayerId);

    this.enabledConfigs.forEach((config) => {
      this.valueByLayerId.set(config.id, {});
      this.tooltipByLayerId.set(config.id, {});
    });
  }

  init(
    map: mapboxgl.Map,
    options?: {
      // biome-ignore lint/suspicious/noConfusingVoidType: migrated from develop
      initializeTooltipHandlers?: (map: mapboxgl.Map) => void | (() => void);
      interactive?: boolean;
    }
  ) {
    this.map = map;
    this.layerManager.init(map);
    this.isInteractive = options?.interactive ?? true;

    const initializeLayers = () => {
      if (!this.map?.isStyleLoaded()) return;
      this.enabledConfigs.forEach((config) => {
        this.layerManager.addSource(config);
        this.layerManager.addLayers(config, '');
      });

      if (options?.initializeTooltipHandlers) {
        const cleanup = options.initializeTooltipHandlers(this.map);
        this.cleanupOverviewHandlers =
          typeof cleanup === 'function' ? cleanup : undefined;
      }

      if (this.isInteractive) {
        this.map?.on('mousemove', this.handleHover);
        this.map?.on('mouseleave', this.handleLeave);
      }
    };

    // Initialize layers only when style is loaded
    if (map.isStyleLoaded()) {
      initializeLayers();
    } else {
      map.once('style.load', initializeLayers);
    }

    map.on('zoomstart', this.handleZoomStart);
    map.on('moveend', this.handleMoveEnd);
    map.on('zoomend', this.handleZoomEnd);

    map.on('idle', this.handleIdle);
    if (map.loaded()) {
      this.isMapLoaded = true;
    } else {
      map.once('load', () => {
        this.isMapLoaded = true;
      });
    }
  }

  private handleZoomStart = () => {
    this.isZooming = true;
  };

  private handleZoomEnd = () => {
    this.isZooming = false;
  };

  private handleMoveEnd = () => {
    if (!this.map) return;
  };

  private handleIdle = () => {
    if (!this.map || !this.isMapLoaded) return;
  };

  private handleHover = (
    e: mapboxgl.MapMouseEvent & { features?: mapboxgl.GeoJSONFeature[] }
  ) => {
    if (!this.map || !this.isInteractive || this.isZooming) {
      this.clearHighlight();
      return;
    }

    const allFeatures = this.map.queryRenderedFeatures(e.point, {
      layers: this.fillLayerIds,
    });

    if (!allFeatures.length) {
      this.clearHighlight();
      return;
    }

    const bestFeature = this.findBestFeature(allFeatures);

    if (!bestFeature) {
      this.clearHighlight();
      return;
    }

    const { config, key, featureId } = bestFeature;
    const newHighlightId = `${config.id}:${key}`;
    const currentHighlightId = this.currentHighlight
      ? `${this.currentHighlight.configId}:${this.currentHighlight.key}`
      : null;

    if (newHighlightId !== currentHighlightId) {
      this.setHighlight(config, key, featureId);
    }

    this.map.getCanvas().style.cursor = 'pointer';
  };

  private findBestFeature(features: mapboxgl.GeoJSONFeature[]): {
    config: ChoroplethLayerConfig;
    key: string;
    featureId: string | number;
  } | null {
    const currentZoom = this.map?.getZoom() ?? 0;
    let bestPriority = 0;
    let bestConfig: ChoroplethLayerConfig | undefined;
    let bestKey: string | undefined;
    let bestFeatureId: string | number | undefined;

    for (const feature of features) {
      const sourceId = String(feature.source);
      const config = this.configBySourceId.get(sourceId);
      if (!config) continue;

      const minZoom = config.minzoom ?? MIN_ZOOM;
      const maxZoom = config.maxzoom ?? MAX_ZOOM;
      if (currentZoom < minZoom || currentZoom >= maxZoom) continue;

      const props = feature.properties || {};

      const key = this.getFeatureKey(config, props);

      const hasData = this.valueByLayerId.get(config.id)?.[key] !== undefined;
      const shouldShow = hasData || ALWAYS_SHOW_TYPES.includes(config.id);

      if (shouldShow) {
        const priority = this.layerPriority[config.id] || 0;
        if (priority > bestPriority) {
          bestPriority = priority;
          bestConfig = config;
          bestKey = key;
          const fallbackId = props[config.promoteId ?? config.propertyName];
          const featureId = feature.id ?? fallbackId;
          bestFeatureId = featureId ?? key;
        }
      }
    }

    if (bestConfig && bestKey !== undefined && bestFeatureId !== undefined) {
      return { config: bestConfig, key: bestKey, featureId: bestFeatureId };
    }
    return null;
  }

  private getFeatureKey(
    config: ChoroplethLayerConfig,
    props: Record<string, unknown>
  ): string {
    return String(props[config.propertyName] ?? '');
  }

  private setHighlight(
    config: ChoroplethLayerConfig,
    key: string,
    featureId: string | number
  ) {
    if (this.currentHighlight) {
      const prevConfig = this.enabledConfigs.find(
        (cfg) => cfg.id === this.currentHighlight?.configId
      );
      if (prevConfig) {
        this.layerManager.setHoverState(
          prevConfig,
          this.currentHighlight.featureId,
          false
        );
      }
    }

    this.layerManager.setHoverState(config, featureId, true);
    this.currentHighlight = { configId: config.id, key, featureId };
  }

  private clearHighlight() {
    if (!this.map) return;

    if (this.currentHighlight) {
      const config = this.enabledConfigs.find(
        (cfg) => cfg.id === this.currentHighlight?.configId
      );
      if (config) {
        this.layerManager.setHoverState(
          config,
          this.currentHighlight.featureId,
          false
        );
      }
      this.currentHighlight = null;
    }

    this.map.getCanvas().style.cursor = '';
  }

  private handleLeave = () => {
    this.clearHighlight();
  };

  update(
    points: MapPoint[],
    metric?: MetricValues,
    extent?: { min: number; max: number } | null,
    options?: {
      msaSelectionList?: SelectionListItem<'msa'>[];
      stateSelectionList?: SelectionListItem<'state'>[];
      mapOptions?: MapPlotOptions;
    }
  ) {
    if (!this.map) return;
    this.performUpdate(points, metric, extent, options);
  }

  private performUpdate(
    points: MapPoint[],
    metric?: MetricValues,
    extent?: { min: number; max: number } | null,
    options?: {
      msaSelectionList?: SelectionListItem<'msa'>[];
      stateSelectionList?: SelectionListItem<'state'>[];
      mapOptions?: MapPlotOptions;
    }
  ) {
    if (!this.map || !this.isMapLoaded) return;

    const colorScheme = getMetricPaletteConfig(metric);

    const userMapOptions = options?.mapOptions ?? {};
    const effectiveMapOptions: MapPlotOptions = {
      normalization: userMapOptions.normalization ?? colorScheme.normalization,
      transformation:
        userMapOptions.transformation ?? colorScheme.transformation,
    };

    const choroplethData = normalizeChoroplethData(
      points,
      this.enabledConfigs,
      {
        msaSelectionList: options?.msaSelectionList,
        stateSelectionList: options?.stateSelectionList,
        metric,
        mapOptions: effectiveMapOptions,
      }
    );

    const { byLayer: valueByLayerId, tooltips: tooltipByLayerId } =
      choroplethData.values;

    const { hasNonMetroData, extraNonDataMsaIds } = choroplethData.geography;

    const colorScales = this.computeColorScales(
      choroplethData.layers,
      colorScheme
    );

    this.valueByLayerId = valueByLayerId;
    this.tooltipByLayerId = tooltipByLayerId;
    this.hasNonMetroData = hasNonMetroData;

    this.enabledConfigs.forEach((config) => {
      const data = this.valueByLayerId.get(config.id) || {};
      const colorScale = colorScales[config.id];
      const { fillColorExpr } = buildFillAndOutline(
        config,
        data,
        colorScale,
        this.hasNonMetroData,
        { extraNonDataMsaIds }
      );
      this.layerManager.setPaint(config, fillColorExpr);
      this.layerManager.setOpacity(config, 1, 1);

      const minz = config.minzoom ?? MIN_ZOOM;
      const maxz = config.maxzoom ?? MAX_ZOOM;
      this.layerManager.setZoomRange(config, minz, maxz);
    });
  }

  destroy() {
    if (!this.map) return;

    if (this.cleanupOverviewHandlers) {
      this.cleanupOverviewHandlers();
      this.cleanupOverviewHandlers = undefined;
    }

    if (this.isInteractive) {
      this.map?.off('mousemove', this.handleHover);
      this.map?.off('mouseleave', this.handleLeave);
    }

    this.map.off('zoomstart', this.handleZoomStart);
    this.map.off('zoomend', this.handleZoomEnd);
    this.map.off('moveend', this.handleMoveEnd);
    this.map.off('idle', this.handleIdle);

    this.layerManager.removeAll(this.enabledConfigs);
  }

  private computeColorScales(
    layers: LayerStatsByLocation,
    colorScheme: ReturnType<typeof getMetricPaletteConfig>
  ): Record<LocationType, (value: number) => string> {
    const defaultColorScale = getColorScaleInfo(
      [],
      colorScheme.type,
      colorScheme.direction,
      colorScheme.domain
    ).color;
    const colorScales: Record<LocationType, (value: number) => string> = {
      [LocationType.REGION]: defaultColorScale,
      [LocationType.COUNTRY]: defaultColorScale,
      [LocationType.STATE]: defaultColorScale,
      [LocationType.COUNTY]: defaultColorScale,
      [LocationType.METRO_AREA]: defaultColorScale,
      [LocationType.CITY]: defaultColorScale,
    };

    this.enabledConfigs.forEach((config) => {
      const stats = layers[config.id];
      const { color } = getColorScaleInfo(
        stats.values,
        colorScheme.type,
        colorScheme.direction,
        colorScheme.domain
      );
      colorScales[config.id] = color;
    });

    return colorScales;
  }
}
